/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery } from '@tanstack/react-query';
import { Bot, Send } from 'lucide-react';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { Badge, Button, ErrorText, Textarea } from './ui';
import { api } from '@/lib/api';

export const useAiStatus = () => useQuery({ queryKey: ['ai-status'], queryFn: () => api.get('/ai/status'), staleTime: 60_000 });

const PROVIDER: Record<string, string> = { openai: 'локальная или OpenAI-совместимая модель', gigachat: 'GigaChat (Сбер)', yandex: 'YandexGPT' };

/** Чат с ИИ; с matterId - модель получает карточку и ленту дела. */
export function AiChat({ matterId }: { matterId?: string }) {
  const { data: st } = useAiStatus();
  const [msgs, setMsgs] = useState<{ role: 'user' | 'assistant'; content: string; meta?: string }[]>([]);
  const [text, setText] = useState('');
  const end = useRef<HTMLDivElement>(null);
  const ask = useMutation({
    mutationFn: (list: typeof msgs) => api.post('/ai/chat', { matterId, messages: list.map(({ role, content }) => ({ role, content })) }),
    onSuccess: (r: any, list) => setMsgs([...list, { role: 'assistant', content: r.text, meta: `${r.model}, ${(r.ms / 1000).toFixed(1)} с` }]),
  });
  useEffect(() => { end.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, ask.isPending]);
  const submit = (e: FormEvent) => { e.preventDefault(); if (!text.trim()) return; const list = [...msgs, { role: 'user' as const, content: text.trim() }]; setMsgs(list); setText(''); ask.mutate(list); };
  if (st && !st.enabled) return <div className="card p-5 text-sm text-slate-600">ИИ не подключён. Администратору: укажите модель в файле <code>data\settings.env</code> (локальная модель, GigaChat или YandexGPT) и перезапустите программу.</div>;
  return (
    <div className="card flex h-[65vh] flex-col">
      <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-2 text-xs text-slate-500">
        <Bot size={16} /> {st ? `${PROVIDER[st.provider] ?? st.provider}: ${st.model}` : '...'}
        {st && (st.local ? <Badge tone="green">данные не покидают компьютер</Badge> : <Badge tone="amber">облачная модель: не вставляйте лишние персональные данные</Badge>)}
        {matterId && <Badge tone="blue">с учётом карточки дела</Badge>}
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {!msgs.length && <div className="text-sm text-slate-500">Например: «Составь план работы конкурсного управляющего на первый месяц», «Какие сделки должника стоит проверить на оспаривание?», «Подготовь текст ходатайства об отложении заседания». Ответы ИИ - черновик: проверяйте нормы и сроки.</div>}
        {msgs.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'ml-auto max-w-[80%] rounded-lg bg-brand-600 px-3 py-2 text-sm text-white' : 'max-w-[90%] rounded-lg bg-slate-100 px-3 py-2 text-sm'}>
            <div className="whitespace-pre-wrap">{m.content}</div>
            {m.meta && <div className="mt-1 text-[11px] text-slate-400">{m.meta}</div>}
          </div>
        ))}
        {ask.isPending && <div className="text-sm text-slate-500">Модель думает... локальная модель на ноутбуке может отвечать до минуты.</div>}
        <ErrorText error={ask.error} />
        <div ref={end} />
      </div>
      <form onSubmit={submit} className="flex gap-2 border-t border-slate-200 p-3">
        <Textarea rows={2} className="flex-1" value={text} onChange={(e) => setText(e.target.value)} placeholder="Вопрос ассистенту" onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit(e as any); }} />
        <Button disabled={ask.isPending || !text.trim()}><Send size={16} /></Button>
      </form>
    </div>
  );
}
